import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useRouteMatch } from 'react-router';

import { MDText } from 'i18n-react';

import { ConfigContext } from 'Context/config';

import { stripStringAndCamelCase } from 'Utilities';
import { usePrevious } from 'Utilities/hooks';
import { REFACTORED_LANDING_PAGE_ROUTES, SELL_MY_CAR_PATH, SELL_YOUR_CAR_PATH } from 'Utilities/wordpress';
import { fetchLandingPage } from 'Utilities/wordpressPages';

import { timeoutFetchSentryGuard } from 'Services/fetch';

import { useLandingPageSections } from 'Hooks/landingPages/useLandingPageSections';
import { useMetaTags } from 'Hooks/misc/useMetaTags';

import texts from '../../../../texts.json';
import { mileageRegex } from '../../homepage/Components/Hero/Hero.constants';
import { MatchParams } from '../@types/landingPages';

const MetaTagT = new MDText(texts);

const metaT = (key: string, options?: Record<string, string>) => MetaTagT.translate(key, options) as string;

export const useLandingPageData = () => {
  const {
    configActions: { update },
    configState: { landingPageData },
  } = useContext(ConfigContext);
  const { pathname } = useLocation();
  const prevPathname = usePrevious(pathname);
  const match = useRouteMatch<MatchParams>();

  const { params, path = '' } = match ?? {};

  const isV2WPPage = REFACTORED_LANDING_PAGE_ROUTES.includes(path?.replace(mileageRegex, ''));

  const wpData = landingPageData?.content;
  const [acf, setAcf] = useState(wpData);
  const [loading, setLoading] = useState(!wpData);
  const [stickyVRM, setStickyVRM] = useState(false);

  const {
    bodyAreaSections,
    processRelatedGuides,
  } = useLandingPageSections(acf);

  const metaData = useMemo(() => {
    if (!acf) {
      return {};
    }

    const sectionName = stripStringAndCamelCase(path ?? '');
    const vehicle = params?.vehicle ?? '';

    return {
      description: acf.seo_description ?? acf.header_description ?? metaT(`landing${sectionName}View.description`, { vehicle }),
      title: acf.seo_title ?? acf.header_title ?? metaT(`landing${sectionName}View.title`, { vehicle }),
    };
  }, [acf, path, params]);

  useMetaTags(metaData, path);

  const getLandingPageUrl = useCallback(() => {
    const isMileagePageWithNoContent = !acf && pathname === `${SELL_MY_CAR_PATH}/mileage`;
    const landingPageUrl = isMileagePageWithNoContent ? pathname.replace(mileageRegex, '') : pathname;

    return landingPageUrl
      .split(`${SELL_MY_CAR_PATH}/`).join('')
      .replace(mileageRegex, '')
      .replace(`${SELL_YOUR_CAR_PATH}/`, 'sell-your-car-');
  }, [acf, pathname]);

  const fetchAcfContent = useCallback(async (landingPageUrl: string) => {
    let apiData;
    setLoading(true);

    try {
      const { content: apiLandingPageData } = await fetchLandingPage(landingPageUrl, isV2WPPage);
      setAcf(apiLandingPageData);
      apiData = apiLandingPageData;
    } catch (error) {
      timeoutFetchSentryGuard(error, () => {
        window?.Sentry?.captureException?.(new Error((error instanceof Error) ? error.message : 'failed to fetch landing page content'), {
          extra: {
            isV2WPPage,
            path,
          },
          level: 'warning',
          tags: {
            fetch: 'Landing Page',
          },
        });
      });
    }
    setLoading(false);
    setStickyVRM(true);
    return apiData;
  }, [isV2WPPage, path]);

  const updateAcfContent = useCallback(async () => {
    const landingPageUrl = getLandingPageUrl();
    await fetchAcfContent(landingPageUrl);
  }, [fetchAcfContent, getLandingPageUrl]);

  useEffect(() => {
    setStickyVRM(true);

    const isLandingPageRoute = prevPathname !== pathname
      && !pathname.endsWith('/mileage')
      && prevPathname !== undefined;

    if (isLandingPageRoute || !acf) {
      updateAcfContent();
    }
  }, [updateAcfContent]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => (
    () => {
      update({ landingPageData: undefined });
    }
  ), [update]);

  return {
    acf,
    bodyAreaSections,
    getRelatedGuidesContent: processRelatedGuides,
    loading,
    stickyVRM,
  };
};
